import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";

export default function Auth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/dashboard");
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        navigate("/dashboard");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast.success("Connexion réussie");
    } catch (error: any) {
      toast.error("Erreur de connexion", {
        description: error.message === "Invalid login credentials" ? "Email ou mot de passe incorrect" : error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-card">
        <CardHeader className="text-center">
          <div className="flex flex-col items-center gap-3 mb-4">
            <img src={brandLogo} alt={BRAND.name.full} className="w-16 h-16" />
            <div>
              <div className="font-cinzel font-bold text-2xl">
                <span className="text-secondary">{BRAND.name.line1}</span>
                <span className="text-primary"> {BRAND.name.line2}</span>
              </div>
              <p className="text-xs font-montserrat uppercase tracking-wide mt-1">
                <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                <span className="text-primary">{BRAND.baseline.parts.text2}</span>
              </p>
            </div>
          </div>
          <h1 className="sr-only">Connexion à l'espace professionnel {BRAND.name.full}</h1>
          <CardDescription className="font-montserrat">Accédez à votre espace professionnel</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signin-email" className="font-montserrat">Email</Label>
              <Input
                id="signin-email"
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
                className="font-montserrat"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signin-password" className="font-montserrat">Mot de passe</Label>
              <Input
                id="signin-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                className="font-montserrat"
              />
            </div>

            <Button type="submit" className="w-full bg-gradient-primary font-montserrat" disabled={loading}>
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="font-montserrat">
              Retour à l'accueil
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
